import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
} from 'react-native';
import LoadingAnimation from '../components/LoadingAnimation';
import PaymentModal from '../components/PaymentModal';
import { useAuth } from '../services/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Icon from 'react-native-vector-icons/MaterialIcons';
import {
  getEventById,
  createRsvp,
  deleteRsvp,
  getEventCheckInForUser,
} from '../services/database';

const EventDetailsScreen = ({ navigation, route }) => {
  const { eventId } = route.params;
  const { user, appUser } = useAuth();
  const { showToast } = useToast();
  
  const [event, setEvent] = useState(null);
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [hasRsvp, setHasRsvp] = useState(false);
  const [rsvpCount, setRsvpCount] = useState(0);
  const [currentUserDbId, setCurrentUserDbId] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [hasCheckedIn, setHasCheckedIn] = useState(false);

  useEffect(() => {
    fetchCurrentUserDbId();
    fetchEventDetails();
  }, [eventId]);

  const fetchCurrentUserDbId = async () => {
    if (appUser?.id) {
      setCurrentUserDbId(appUser.id);
    }
  };

  const fetchEventDetails = async () => {
    try {
      setLoading(true);
      
      const eventData = await getEventById(eventId);
      // Map snake_case to camelCase for UI compatibility
      const mapped = {
        ...eventData,
        eventDate: eventData.event_date,
        eventTime: eventData.event_time,
        endTime: eventData.end_time,
        locationLatitude: eventData.location_latitude,
        locationLongitude: eventData.location_longitude,
        locationAddress: eventData.location_address,
        eventType: eventData.event_type,
        isPaid: eventData.is_paid,
        stripeFeePercentage: eventData.stripe_fee_percentage,
        clubId: eventData.club_id,
        checkInMode: eventData.check_in_mode || 'OFF',
        club: eventData.club ? {
          ...eventData.club,
          clubName: eventData.club.club_name,
          profilePictureUrl: eventData.club.profile_picture_url,
          stripeAccountId: eventData.club.stripe_account_id,
          stripeOnboardingComplete: eventData.club.stripe_onboarding_complete,
        } : null,
        rsvps: { items: eventData.rsvps || [] },
      };
      setEvent(mapped);
      setClub(mapped.club);
      setRsvpCount(mapped.rsvps?.items?.length || 0);
    } catch (error) {
      console.error('Error fetching event details:', error);
      showToast('Failed to load event details', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if current user has RSVP'd after both event and user ID are loaded
    if (event && currentUserDbId) {
      const userRsvp = event.rsvps?.items?.find(r => r.user_id === currentUserDbId || r.userId === currentUserDbId);
      setHasRsvp(!!userRsvp);
    }
  }, [event, currentUserDbId]);

  useEffect(() => {
    if (!eventId || !currentUserDbId) return;
    let cancelled = false;
    getEventCheckInForUser(eventId, currentUserDbId).then((row) => {
      if (!cancelled) setHasCheckedIn(!!row);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [eventId, currentUserDbId]);

  const handlePayment = async () => {
    if (!currentUserDbId) {
      showToast('Please log in to register', 'error');
      return;
    }

    if (!event.club?.stripeAccountId) {
      showToast('This club has not set up payments yet', 'error');
      return;
    }

    // Show payment modal
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = async (paymentIntentId, amountPaid) => {
    try {
      await createRsvp({
        event_id: eventId,
        user_id: currentUserDbId,
        has_paid: true,
        amount_paid: amountPaid,
        payment_intent_id: paymentIntentId,
      });
      
      setHasRsvp(true);
      setRsvpCount(prev => prev + 1);
      setShowPaymentModal(false);
      
      // Refresh event details to get updated data
      await fetchEventDetails();
    } catch (error) {
      console.error('Error creating RSVP after payment:', error);
      showToast('Payment successful but failed to register. Please contact support.', 'error');
    }
  };

  const handleRSVP = async () => {
    if (!currentUserDbId) {
      showToast('Please log in to RSVP', 'error');
      return;
    }

    // If paid event, redirect to payment flow
    if (event?.isPaid && !hasRsvp) {
      handlePayment();
      return;
    }

    try {
      setRsvpLoading(true);

      if (hasRsvp) {
        // Cancel RSVP
        await deleteRsvp(eventId, currentUserDbId);
        setHasRsvp(false);
        setRsvpCount(prev => prev - 1);
        showToast('RSVP cancelled', 'success');
      } else {
        // Create RSVP
        await createRsvp({
          event_id: eventId,
          user_id: currentUserDbId,
          has_paid: false,
          amount_paid: null,
          payment_intent_id: null,
        });
        setHasRsvp(true);
        setRsvpCount(prev => prev + 1);
        showToast('RSVP confirmed!', 'success');
      }
    } catch (error) {
      console.error('Error toggling RSVP:', error);
      showToast('Failed to update RSVP', 'error');
    } finally {
      setRsvpLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'pm' : 'am';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes}${ampm}`;
  };

  if (loading) {
    return (
      <View style={styles.centered}>
        <LoadingAnimation size={525} />
      </View>
    );
  }

  if (!event) {
    return (
      <View style={styles.centered}>
        <Icon name="error-outline" size={48} color="#f44336" />
        <Text style={styles.errorText}>Event not found</Text>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Text style={styles.backButtonText}>Go Back</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const eventLocation = event.locationAddress || event.location || 'Location TBD';
  const eventDate = formatDate(event.eventDate);
  const startTime = formatTime(event.eventTime);
  const endTime = event.endTime ? formatTime(event.endTime) : null;

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backIcon}>
          <Icon name="arrow-back" size={24} color="#333" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Event Details</Text>
        <View style={{ width: 24 }} />
      </View>

      {/* Event Title */}
      <Text style={styles.title}>{event.title}</Text>

      {/* Club Info */}
      <TouchableOpacity
        style={styles.clubCard}
        onPress={() => {
          navigation.navigate('Map', { 
            screen: 'Map',
            params: { selectedUserId: club.id }
          });
        }}
      >
        <View style={styles.clubInfo}>
          <Icon name="groups" size={24} color="#94EB98" />
          <View style={styles.clubText}>
            <Text style={styles.clubName}>{club.clubName || club.username}</Text>
            <Text style={styles.clubSubtext}>View Club Profile</Text>
          </View>
        </View>
        <Icon name="chevron-right" size={24} color="#999" />
      </TouchableOpacity>

      {/* Date & Time */}
      <View style={styles.section}>
        <View style={styles.infoRow}>
          <Icon name="event" size={20} color="#94EB98" />
          <Text style={styles.infoText}>{eventDate}</Text>
        </View>
        <View style={styles.infoRow}>
          <Icon name="schedule" size={20} color="#94EB98" />
          <Text style={styles.infoText}>
            {startTime}{endTime ? ` - ${endTime}` : ''}
          </Text>
        </View>
        <View style={styles.infoRow}>
          <Icon name="location-on" size={20} color="#94EB98" />
          <Text style={styles.infoText}>{eventLocation}</Text>
        </View>
        {event.capacity && (
          <View style={styles.infoRow}>
            <Icon name="people" size={20} color="#94EB98" />
            <Text style={styles.infoText}>
              Capacity: {rsvpCount} / {event.capacity}
            </Text>
          </View>
        )}
      </View>

      {/* Price */}
      {event.isPaid && (
        <View style={styles.priceSection}>
          <Icon name="attach-money" size={24} color="#FF9800" />
          <Text style={styles.priceText}>${event.price?.toFixed(2)}</Text>
        </View>
      )}

      {/* Description */}
      {event.description && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>About</Text>
          <Text style={styles.description}>{event.description}</Text>
        </View>
      )}

      {/* RSVP/Payment Button */}
      <TouchableOpacity
        style={[
          styles.rsvpButton, 
          hasRsvp && styles.rsvpButtonActive,
          rsvpLoading && styles.rsvpButtonDisabled,
          event?.isPaid && !hasRsvp && styles.paymentButton
        ]}
        onPress={handleRSVP}
        disabled={rsvpLoading}
        activeOpacity={0.8}
      >
        {rsvpLoading ? (
          <ActivityIndicator size="small" color="white" />
        ) : (
          <>
            {event?.isPaid && !hasRsvp ? (
              <>
                <Icon name="payment" size={20} color="white" />
                <Text style={styles.rsvpButtonText}>
                  Pay ${event.price?.toFixed(2)} to Register
                </Text>
              </>
            ) : (
              <>
                <Icon name={hasRsvp ? "check-circle" : "event-available"} size={20} color="white" />
                <Text style={styles.rsvpButtonText}>
                  {hasRsvp ? 'RSVP\'d ✓' : 'RSVP'}
                </Text>
              </>
            )}
          </>
        )}
      </TouchableOpacity>

      {/* Attendees Count */}
      <View style={styles.attendeesSection}>
        <Icon name="people" size={18} color="#666" />
        <Text style={styles.attendeesText}>
          {rsvpCount} {rsvpCount === 1 ? 'person' : 'people'} {hasRsvp ? 'including you' : ''} going
        </Text>
      </View>

      {/* Check-in: club owner */}
      {event?.clubId === appUser?.id && (event?.checkInMode === 'QR_CODE' || event?.checkInMode === 'LOCATION') && (
        <TouchableOpacity
          style={styles.checkInButton}
          onPress={() => navigation.navigate('EventCheckIn', { eventId })}
          activeOpacity={0.8}
        >
          <Icon name="qr-code" size={20} color="#fff" />
          <Text style={styles.checkInButtonText}>Manage Check-In</Text>
        </TouchableOpacity>
      )}

      {/* Check-in: player — show when QR mode and user is not the club */}
      {event?.clubId !== appUser?.id && event?.checkInMode === 'QR_CODE' && currentUserDbId && (
        <View style={styles.checkInBlock}>
          {hasCheckedIn ? (
            <View style={styles.checkedInBadge}>
              <Icon name="check-circle" size={20} color="#0a0" />
              <Text style={styles.checkedInBadgeText}>You're checked in</Text>
            </View>
          ) : (
            <TouchableOpacity
              style={styles.checkInButton}
              onPress={() => navigation.navigate('CheckInScanner', { eventId })}
              activeOpacity={0.8}
            >
              <Icon name="qr-code-scanner" size={20} color="#fff" />
              <Text style={styles.checkInButtonText}>Check In</Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Payment Modal */}
      {event?.isPaid && (
        <PaymentModal
          visible={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          event={event}
          userId={currentUserDbId}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  contentContainer: {
    paddingBottom: 40,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
    color: '#666',
  },
  errorText: {
    marginTop: 12,
    fontSize: 18,
    color: '#f44336',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backIcon: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#333',
    padding: 20,
    backgroundColor: 'white',
    marginBottom: 12,
  },
  clubCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'white',
    padding: 16,
    marginHorizontal: 12,
    marginBottom: 12,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  clubInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  clubText: {
    flex: 1,
  },
  clubName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
  },
  clubSubtext: {
    fontSize: 13,
    color: '#999',
    marginTop: 2,
  },
  section: {
    backgroundColor: 'white',
    padding: 20,
    marginHorizontal: 12,
    marginBottom: 12,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  infoText: {
    fontSize: 16,
    color: '#333',
    flex: 1,
  },
  priceSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'white',
    padding: 16,
    marginHorizontal: 12,
    marginBottom: 12,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  priceText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#FF9800',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 12,
  },
  description: {
    fontSize: 15,
    color: '#666',
    lineHeight: 22,
  },
  rsvpButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#94EB98',
    paddingVertical: 16,
    marginHorizontal: 12,
    marginTop: 12,
    borderRadius: 10,
    shadowColor: '#94EB98',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
  },
  rsvpButtonDisabled: {
    opacity: 0.6,
  },
  paymentButton: {
    backgroundColor: '#FF9800',
    shadowColor: '#FF9800',
  },
  rsvpButtonActive: {
    backgroundColor: '#76AF87',
  },
  rsvpButtonText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: 'white',
  },
  attendeesSection: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    marginTop: 16,
  },
  attendeesText: {
    fontSize: 14,
    color: '#666',
  },
  checkInBlock: {
    marginTop: 12,
    marginHorizontal: 16,
  },
  checkInButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 20,
    backgroundColor: '#333',
    borderRadius: 10,
  },
  checkInButtonText: {
    fontSize: 16,
    color: '#fff',
    fontWeight: '600',
  },
  checkedInBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 20,
    backgroundColor: '#e8f5e9',
    borderRadius: 10,
  },
  checkedInBadgeText: {
    fontSize: 16,
    color: '#0a0',
    fontWeight: '600',
  },
  backButton: {
    backgroundColor: '#94EB98',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 10,
    marginTop: 20,
  },
  backButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
});

export default EventDetailsScreen;

